// src/pages/Admin/Finance/PaymentOptions/PaymentOptionsCreate.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./PaymentOptionsCreate.module.scss";

const IconArrowLeft = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M14.7 6.3a1 1 0 0 1 0 1.4L11.41 11H20a1 1 0 1 1 0 2h-8.59l3.29 3.3a1 1 0 1 1-1.4 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.4 0z"
      fill="currentColor"
    />
  </svg>
);

const IconPlus = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path
      d="M11 5a1 1 0 0 1 2 0v6h6a1 1 0 1 1 0 2h-6v6a1 1 0 1 1-2 0v-6H5a1 1 0 1 1 0-2h6V5z"
      fill="currentColor"
    />
  </svg>
);

const PaymentOptionsCreate = () => {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const navigate = useNavigate();

  const statusLabel = useMemo(() => (isActive ? "Active" : "Inactive"), [isActive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);

    if (!name.trim() || !title.trim()) {
      setErr("Name and Title are required.");
      return;
    }

    try {
      setLoading(true);

      await api.post("/v1/payment-options", {
        name: name.trim(),
        title: title.trim(),
        description,
        instruction,
        isActive,
      });

      navigate("/admin/finance/payment-options");
    } catch (error) {
      console.error("Failed to create payment option:", error);

      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.msg ||
        "Failed to create payment option.";

      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => navigate("/admin/finance/payment-options");

  return (
    <div className={cls.wrap}>
      <header className={cls.header}>
        <div className={cls.headLeft}>
          <h1 className={cls.pageTitle}>Create Payment Option</h1>
          <p className={cls.pageSub}>Add a new payment method for members</p>

          <div className={cls.badges}>
            <span className={`${cls.badge} ${cls.badgeKey}`}>
              {name?.trim() ? name.trim() : "key"}
            </span>

            <span
              className={`${cls.badge} ${isActive ? cls.badgeActive : cls.badgeInactive}`}
            >
              {statusLabel}
            </span>
          </div>
        </div>

        <button type="button" className={cls.backBtn} onClick={goBack} disabled={loading}>
          <IconArrowLeft />
          Back
        </button>
      </header>

      {err && <div className={cls.error}>{err}</div>}

      <form className={cls.form} onSubmit={handleSubmit}>
        <section className={cls.card}>
          <div className={cls.cardHead}>
            <h2 className={cls.cardTitle}>Basics</h2>
            <p className={cls.cardHint}>How this option appears in the app</p>
          </div>

          <div className={cls.grid2}>
            <div className={cls.field}>
              <label>Name (unique key)</label>
              <input
                type="text"
                placeholder="Example: etransfer, cash, crypto"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />
              <p className={cls.note}>
                Keep it unique and stable. Example: etransfer, paxum, crypto_wallet.
              </p>
            </div>

            <div className={cls.field}>
              <label>Title (Display Name)</label>
              <input
                type="text"
                placeholder="Example: E-Transfer / Bank Transfer"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="off"
              />
              <p className={cls.note}>Short and clean. This shows to members.</p>
            </div>
          </div>

          <div className={cls.field}>
            <label>Description</label>
            <textarea
              placeholder="Short description about this payment method..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className={cls.field}>
            <label>Instruction</label>
            <textarea
              placeholder="How to pay using this method..."
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              rows={4}
            />
          </div>
        </section>

        <section className={cls.card}>
          <div className={cls.cardHead}>
            <h2 className={cls.cardTitle}>Status</h2>
            <p className={cls.cardHint}>Show or hide this payment option</p>
          </div>

          <div className={cls.statusRow}>
            <div className={cls.statusLeft}>
              <div className={cls.statusTitle}>Availability</div>
              <div className={cls.statusSub}>
                Active options are visible during checkout/payment steps.
              </div>
            </div>

            <div className={cls.statusPill} role="group" aria-label="Status">
              <button
                type="button"
                className={`${cls.pillBtn} ${isActive ? cls.pillOn : ""}`}
                onClick={() => setIsActive(true)}
                aria-pressed={isActive}
                disabled={loading}
              >
                Active
              </button>
              <button
                type="button"
                className={`${cls.pillBtn} ${!isActive ? cls.pillOff : ""}`}
                onClick={() => setIsActive(false)}
                aria-pressed={!isActive}
                disabled={loading}
              >
                Inactive
              </button>
            </div>
          </div>
        </section>

        <div className={cls.actions}>
          <button
            type="button"
            className={`${cls.actionBtn} ${cls.ghost}`}
            onClick={goBack}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`${cls.actionBtn} ${cls.primary}`}
            disabled={loading}
          >
            <IconPlus />
            {loading ? "Creating…" : "Create Payment Option"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentOptionsCreate;
